import React, { FC, ReactNode, useEffect } from 'react';
import { range } from 'lodash';
import { Link, Skeleton } from '@mui/material';
import { formatCurrency } from '@factorin/util';

import { useAction, useSelector } from 'Modules/core/redux';
import { useTranslation } from 'Modules/core/i18n';
import {
    Typography,
    Stack,
    useDesktop,
    QuestionRound24,
    GridContainer,
    GridItem,
    useMobile,
    useMediaSwitch,
    Tooltip,
} from 'Modules/ui3';

import { ContentBox } from '../ContentBox';
import { LANDING_DOMAIN_PATH } from '../../constants';
import { MonitorCardsCodes, MonitoringElement } from '../../api';
import { monitoringActions } from '../../state/monitoring';
import { SectionTitleH1 } from '../Titles';
import { Paragraph } from '../Paragraph';


type UpdatedElements = Pick<MonitoringElement, 'text' | 'title'> & Partial<Pick<MonitoringElement, 'link'>> & {
    tooltip: ReactNode;
};

interface UpdatedData {
    users: string;
    date: string;
    elements: UpdatedElements[];
}


const SKELETONS_COUNT = 6;

const TOOLTIP_CODES = {
    [MonitorCardsCodes.K1]: 'tooltips.K1',
    [MonitorCardsCodes.K2]: 'tooltips.K2',
    [MonitorCardsCodes.K3]: 'tooltips.K3',
    [MonitorCardsCodes.K4]: 'tooltips.K4',
    [MonitorCardsCodes.K5]: 'tooltips.K5',
};


const ElementWrapper: FC = ({ children }) => {
    const gridItemSpacing = useMediaSwitch(52, 32, 30);
    const isDesktop = useDesktop();

    return (
        <GridItem
            desktop={1}
            tablet={1.5}
            mobile={4}
            height={({ spacing }) => spacing(gridItemSpacing)}
        >
            <ContentBox p={isDesktop ? 8 : 4} height='100%' border='none'>
                <Stack justifyContent='space-between' height='100%'>
                    {children}
                </Stack>
            </ContentBox>
        </GridItem>
    );
};


export const MonitoringBlock: FC = () => {
    const { t } = useTranslation('monitoring');
    const isDesktop = useDesktop();
    const isMobile = useMobile();
    const monitorElements = useSelector<MonitoringElement[]>(`${LANDING_DOMAIN_PATH}.monitoring`);
    const getMonitoringList = useAction(monitoringActions.getMonitoringList);

    useEffect(() => {
        getMonitoringList();
    }, [getMonitoringList]);

    const updatedData = monitorElements.reduce<UpdatedData>((result, {
        code, title, text, link,
    }) => {
        if (code === MonitorCardsCodes.K7)
            result.date = text;
        else if (code === MonitorCardsCodes.K8)
            result.users = formatCurrency(text).replace(' ', '&nbsp;');
        else {
            result.elements.push({
                title,
                text,
                tooltip: t(TOOLTIP_CODES[code]),
                ...code === MonitorCardsCodes.K6 && { link },
            });
        }
        return result;
    }, {
        users: '',
        date: '',
        elements: [],
    });

    return (
        <>
            <Typography
                fontSize={isDesktop ? '40px' : isMobile ? '28px' : '32px'}
                lineHeight='1.5'
                fontWeight='500'
                color='grayscale.shade95'
                py={isDesktop ? 30 : 20}
                textAlign='center'
                mt={isDesktop ? 16 : 12}
                sx={{
                    '&>span>span': {
                        color: 'general.primary50',
                    },
                }}
            >
                {updatedData.users ? t('users', { count: updatedData.users })
                    : (
                        <Stack component='span' alignItems='center'>
                            <Skeleton width='70%' />
                            <Skeleton width='60%' />
                        </Stack>
                    )}
            </Typography>
            <SectionTitleH1>
                {t('title')}
            </SectionTitleH1>
            <Stack
                justifyContent='space-between'
                alignItems={!isMobile ? 'end' : 'start'}
                {...!isMobile && { direction: 'row' }}
                {...isMobile && { spacing: 6 }}
            >
                <Paragraph color='grayscale.shade90' maxWidth={useMediaSwitch('45%', '75%', '100%')}>
                    {t('description')}
                </Paragraph>
                {updatedData.date && (
                    <Typography color='grayscale.shade90' variant='regularBody2'>
                        {updatedData.date}
                    </Typography>
                )}
            </Stack>
            <GridContainer spacing={2} columns={3} mt={isDesktop ? 10 : 4} justifyContent='center'>
                {!updatedData.elements.length
                    ? range(SKELETONS_COUNT).map((_, index) => (
                        <ElementWrapper key={index}>
                            <Skeleton width='100%' />
                            <Skeleton width='100%' />
                        </ElementWrapper>
                    ))
                    : updatedData.elements.map(({ title, text, link, tooltip }, index) => (
                        <ElementWrapper key={index}>
                            <Typography component='h3' variant={isDesktop ? 'boldTitle4' : 'mediumHeader2'} lineHeight='1.5' color='grayscale.shade95'>
                                {title}
                            </Typography>
                            <Stack
                                direction='row'
                                justifyContent='space-between'
                                alignItems='baseline'
                                spacing={isDesktop ? 3 : 7.5}
                            >
                                <Typography
                                    variant={isDesktop ? 'regularBody2' : 'regularCaption1'}
                                    lineHeight={isDesktop ? '1.5' : '1.4'}
                                    color='grayscale.shade90'
                                    mt={isDesktop ? 3 : 2}
                                >
                                    {text}
                                    {link && (
                                        <Link href={link} underline='none'>
                                            <Typography variant={isDesktop ? 'regularBody2' : 'regularCaption1'} lineHeight={isDesktop ? '1.5' : '1.4'} color='general.primary50'>&nbsp;«Новости»</Typography>
                                        </Link>
                                    )}
                                </Typography>
                                {tooltip && (
                                    <Tooltip
                                        text={tooltip}
                                        textProps={{
                                            variant: 'regularCaption2',
                                        }}
                                        componentPropsTooltipSx={{
                                            width: theme => theme.spacing(65),
                                        }}
                                    >
                                        <Stack alignSelf='end'>
                                            <QuestionRound24 fontSize='large' sx={{ color: 'grayscale.shade85' }} />
                                        </Stack>
                                    </Tooltip>
                                )}
                            </Stack>
                        </ElementWrapper>
                    ))
                }
            </GridContainer>
        </>
    );
};
